import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, startAfter, getDocs,
  doc, updateDoc, serverTimestamp, type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { BellOff } from 'lucide-react';
import { notificationIcon } from './notificationIcons';
import { db } from '../../shared/lib/firebase';
import { useAuth } from '../auth/useAuth';
import { Button } from '../../shared/ui/Button';
import { Spinner } from '../../shared/ui/Spinner';
import { Skeleton } from '../../shared/ui/Skeleton';
import { EmptyState } from '../../shared/ui/EmptyState';
import type { NotificationDoc, NotificationType } from './types';
import { NOTIFICATION_GROUPS } from './types';
import { timeAgo } from '../../shared/utils/timeAgo';
import styles from './NotificationsView.module.css';

const PAGE = 25;
const ALL_TYPES = NOTIFICATION_GROUPS.flatMap((g) => g.types);

export function NotificationsView() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const [items, setItems] = useState<NotificationDoc[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<NotificationType | 'all'>('all');
  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>('all');
  const navigate = useNavigate();

  async function loadMore(reset = false) {
    if (!uid || loading) return;
    setLoading(true);
    try {
      const base = reset || !cursor
        ? query(collection(db, 'notifications'), where('userId', '==', uid), orderBy('createdAt', 'desc'), limit(PAGE))
        : query(collection(db, 'notifications'), where('userId', '==', uid), orderBy('createdAt', 'desc'), startAfter(cursor), limit(PAGE));
      const snap = await getDocs(base);
      const page = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<NotificationDoc, 'id'>) }));
      setItems((prev) => (reset ? page : [...prev, ...page]));
      setCursor(snap.docs[snap.docs.length - 1] ?? null);
      setDone(snap.docs.length < PAGE);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!uid) return;
    void (async () => {
      await loadMore(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const filtered = items.filter((n) =>
    (typeFilter === 'all' || n.type === typeFilter) &&
    (readFilter === 'all' || (readFilter === 'unread' ? !n.read : n.read)),
  );

  async function handleClick(n: NotificationDoc) {
    if (!n.read) {
      await updateDoc(doc(db, 'notifications', n.id), { read: true, readAt: serverTimestamp() });
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) navigate(n.link);
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Notificações</h1>
        <div className={styles.filters}>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as NotificationType | 'all')}>
            <option value="all">Todos os tipos</option>
            {ALL_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={readFilter} onChange={(e) => setReadFilter(e.target.value as 'all' | 'unread' | 'read')}>
            <option value="all">Todas</option>
            <option value="unread">Não lidas</option>
            <option value="read">Lidas</option>
          </select>
        </div>
      </header>

      {filtered.length === 0 && !loading && (
        <EmptyState
          icon={<BellOff size={40} />}
          title="Nenhuma notificação"
          description={done ? 'Você está em dia. Novas notificações aparecem aqui.' : 'Nenhuma notificação nesta página.'}
        />
      )}
      <ul className={styles.list}>
        {filtered.map((n) => {
          const Icon = notificationIcon(n.type);
          return (
            <li key={n.id} className={`${styles.item} ${n.read ? '' : styles.unread}`}>
              <button type="button" className={styles.itemBtn} onClick={() => void handleClick(n)}>
                <span className={`${styles.icon} ${styles[`p-${n.priority}`]}`}><Icon size={18} /></span>
                <span className={styles.body}>
                  <span className={styles.itemTitle}>{n.title}</span>
                  <span className={styles.text}>{n.body}</span>
                </span>
                <span className={styles.time}>{timeAgo(n.createdAt?.toMillis?.())}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {loading && items.length === 0 && (
        <div className={styles.skeletonList}>
          <Skeleton count={6} height={60} />
        </div>
      )}
      {loading && items.length > 0 && (
        <div className={styles.center}><Spinner /></div>
      )}
      {!done && !loading && (
        <div className={styles.center}>
          <Button variant="secondary" onClick={() => void loadMore(false)}>Carregar mais</Button>
        </div>
      )}
    </div>
  );
}
